import React from 'react';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import BackMobileIcon from '@/assets/images/level-detail/back-mobile.svg';
import BackTabletIcon from '@/assets/images/level-detail/back-tablet.svg';
import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type LevelDetailHeaderProps = {
  level: number;
  progressPercent: number;
  statusLabel: string;
  onBackPress: () => void;
};

export function LevelDetailHeader({
  level,
  progressPercent,
  statusLabel,
  onBackPress,
}: LevelDetailHeaderProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const normalizedProgress = Math.max(0, Math.min(100, progressPercent));
  const isCompleted = statusLabel === STRINGS.guardianPlayer.levelDetail.levelStatus.completed;
  const statusPillStyle = isCompleted ? styles.completedStatusPill : styles.inProgressStatusPill;
  const statusTextStyle = isCompleted ? styles.completedStatusText : styles.inProgressStatusText;

  if (isTablet) {
    return (
      <Stack
        direction="row"
        align="center"
        style={[styles.tabletHeader, { height: insets.top + scale(72), paddingTop: insets.top }]}>
        <Pressable onPress={onBackPress} style={styles.tabletBack}>
          <BackTabletIcon width={scale(24)} height={scale(24)} />
          <Text variant="Font Inter/Subtitle/Medium 14px" style={styles.tabletBackText}>
            {STRINGS.guardianPlayer.levelDetail.back}
          </Text>
        </Pressable>

        <Stack flex={1} align="center" justify="center" gap={scale(8)}>
          <Stack direction="row" align="center" gap={scale(8)}>
            <Text variant="Font Inter/Subtitle/Bold 14px" style={styles.tabletTitle}>
              {STRINGS.guardianPlayer.levelDetail.levelTitle(level)}
            </Text>
            <Stack style={[styles.statusPill, statusPillStyle]}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                style={statusTextStyle}>
                {statusLabel}
              </Text>
            </Stack>
          </Stack>
          <ProgressLabel percent={normalizedProgress} />
        </Stack>

        <Stack align="center" justify="center" style={styles.moreButton} />
      </Stack>
    );
  }

  return (
    <Stack
      style={[
        styles.mobileHeader,
        {
          height: insets.top + scale(116),
          paddingTop: insets.top,
        },
      ]}>
      <Stack direction="row" align="center" justify="space-between" style={styles.mobileNav}>
        <Pressable onPress={onBackPress} style={styles.mobileCircleButton}>
          <BackMobileIcon width={scale(24)} height={scale(24)} />
        </Pressable>
        <Stack align="center" gap={scale(4)}>
          <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.mobileTitle}>
            {STRINGS.guardianPlayer.levelDetail.levelTitle(level)}
          </Text>
          <Stack style={[styles.statusPill, statusPillStyle]}>
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={statusTextStyle}>
              {statusLabel}
            </Text>
          </Stack>
        </Stack>
        <Stack style={styles.mobileCircleButtonGhost} />
      </Stack>

      <Stack gap={scale(8)} style={styles.mobileProgressBlock}>
        <ProgressLabel percent={normalizedProgress} />
        <Stack style={styles.progressTrack}>
          <Stack style={[styles.progressFill, { width: `${normalizedProgress}%` }]} />
        </Stack>
      </Stack>
    </Stack>
  );
}

function ProgressLabel({ percent }: { percent: number }) {
  const styles = useStyles();

  return (
    <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.progressLabel}>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
        style={styles.progressPercent}>
        {percent}%
      </Text>
      {` ${STRINGS.guardianPlayer.levelDetail.progressThroughSuffix}`}
    </Text>
  );
}

const useStyles = createStyles(({ scale }) => ({
  tabletHeader: {
    height: scale(72),
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['3'],
    backgroundColor: colors['0'],
    gap: scale(30),
  },
  tabletBack: {
    height: scale(40),
    minWidth: scale(92),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(10),
  },
  tabletBackText: {
    color: colors.Blue,
  },
  tabletTitle: {
    color: colors.Blue,
  },
  statusPill: {
    height: scale(24),
    justifyContent: 'center',
    paddingHorizontal: scale(10),
    borderRadius: scale(30),
  },
  inProgressStatusPill: {
    backgroundColor: 'rgba(255, 170, 0, 0.1)',
  },
  completedStatusPill: {
    backgroundColor: 'rgba(45, 151, 52, 0.1)',
  },
  inProgressStatusText: {
    color: guardianColors['12'],
  },
  completedStatusText: {
    color: colors.Success,
  },
  moreButton: {
    width: scale(92),
    height: scale(40),
    opacity: 0.95,
  },
  moreText: {
    color: colors.Blue,
  },
  mobileHeader: {
    backgroundColor: colors['0'],
  },
  mobileNav: {
    height: scale(62),
    paddingHorizontal: scale(20),
  },
  mobileCircleButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['1'],
  },
  mobileCircleButtonGhost: {
    width: scale(40),
    height: scale(40),
  },
  mobileTitle: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  mobileProgressBlock: {
    paddingHorizontal: scale(20),
    marginTop: scale(8),
  },
  progressLabel: {
    color: guardianColors['6'],
  },
  progressPercent: {
    color: guardianColors['10'],
  },
  progressTrack: {
    height: scale(8),
    borderRadius: scale(10),
    backgroundColor: guardianColors['3'],
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: scale(10),
    backgroundColor: colors.Blue,
  },
}));
